import React from "react";

const SyncPreviewModal = ({ show, onClose, onConfirm, previewData, syncing }) => {
  if (!show || !previewData) return null;

  const {
    bundleName,
    bundleClasses,
    applicableStudentTypes,
    studentType,
    gender,
    academicYear,
    items = [],
    summary = {}
  } = previewData;

  const targetStudentTypes = applicableStudentTypes || studentType;
  const toCreate = summary.toCreate || 0;
  const toUpdate = summary.toUpdate || 0;
  const hasChanges = toCreate > 0 || toUpdate > 0;

  return (
    <div
      className={`modal fade ${show ? "show d-block" : "d-none"}`}
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.55)", zIndex: 1060 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
          {/* Header */}
          <div className="modal-header border-0 bg-light pb-2">
            <div>
              <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                <i className="bi bi-arrow-repeat text-primary"></i> Sync Preview: {bundleName}
              </h5>
              <div className="d-flex flex-wrap gap-3 text-muted small mt-1">
                <span>
                  Academic Year: <strong className="text-dark">{academicYear || "N/A"}</strong>
                </span>
                <span>
                  Gender: <strong className="text-dark">{gender || "N/A"}</strong>
                </span>
                <span>
                  Student Types: <strong className="text-dark">{targetStudentTypes || "N/A"}</strong>
                </span>
                <span>
                  Suitable Classes: <strong className="text-dark">{bundleClasses || "None"}</strong>
                </span>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              disabled={syncing}
              onClick={onClose}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body py-3">
            {/* Summary Cards */}
            <div className="row g-2 mb-3">
              <div className="col-4">
                <div className="p-2 rounded border bg-success bg-opacity-10 text-center">
                  <div className="text-success fw-bold fs-5">{toCreate}</div>
                  <div className="text-muted small">Will Create</div>
                </div>
              </div>
              <div className="col-4">
                <div className="p-2 rounded border bg-primary bg-opacity-10 text-center">
                  <div className="text-primary fw-bold fs-5">{toUpdate}</div>
                  <div className="text-muted small">Will Update (Classes / Student Types)</div>
                </div>
              </div>
              <div className="col-4">
                <div className="p-2 rounded border bg-secondary bg-opacity-10 text-center">
                  <div className="text-secondary fw-bold fs-5">{summary.noChange || 0}</div>
                  <div className="text-muted small">No Change</div>
                </div>
              </div>
            </div>

            {!hasChanges ? (
              <div className="alert alert-secondary py-2 px-3 small d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-check-circle-fill text-success fs-5"></i>
                <span>
                  All products in this bundle are already synced and up to date for the specified classes and student types. No updates needed.
                </span>
              </div>
            ) : (
              <p className="text-muted small mb-2">
                Review changes before updating the database. Existing single products will have the bundle's classes and student types merged without creating duplicate items:
              </p>
            )}

            {/* Table */}
            <div className="table-responsive border rounded" style={{ maxHeight: "400px" }}>
              <table className="table table-hover align-middle mb-0 text-sm">
                <thead className="table-light sticky-top">
                  <tr>
                    <th scope="col" style={{ width: "25%" }}>Product</th>
                    <th scope="col" style={{ width: "15%" }}>Action</th>
                    <th scope="col" style={{ width: "30%" }}>Suitable Classes</th>
                    <th scope="col" style={{ width: "30%" }}>Student Types</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">
                        No products found in this bundle.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, idx) => {
                      const isCreate = item.action === "CREATE";
                      const isUpdate = item.action === "UPDATE";
                      const isNoChange = item.action === "NO_CHANGE";

                      return (
                        <tr key={idx}>
                          <td>
                            <div className="fw-semibold text-dark">{item.productName}</div>
                            {item.displayName && item.displayName !== item.productName && (
                              <div className="text-muted small">{item.displayName}</div>
                            )}
                          </td>
                          <td>
                            {isCreate && (
                              <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                                <i className="bi bi-plus-circle me-1"></i> Create New
                              </span>
                            )}
                            {isUpdate && (
                              <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
                                <i className="bi bi-pencil me-1"></i> Update
                              </span>
                            )}
                            {isNoChange && (
                              <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1">
                                <i className="bi bi-check2-circle me-1"></i> No Change
                              </span>
                            )}
                          </td>
                          <td>
                            {isCreate && (
                              <div className="small">
                                <span className="text-muted">Will assign: </span>
                                <span className="fw-semibold text-dark">{item.newClasses || "None"}</span>
                              </div>
                            )}
                            {isUpdate && (
                              <div className="small">
                                {item.classesToAdd && item.classesToAdd.length > 0 ? (
                                  <>
                                    <div>
                                      <span className="text-muted">Adding: </span>
                                      <span className="badge bg-primary text-white me-1">
                                        +{item.classesToAdd.join(", ")}
                                      </span>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                      Current: <span className="text-dark">{item.currentClasses}</span> &rarr; Result:{" "}
                                      <span className="fw-semibold text-dark">{item.newClasses}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-muted">
                                    Same classes (<span className="text-dark">{item.currentClasses}</span>)
                                  </div>
                                )}
                              </div>
                            )}
                            {isNoChange && (
                              <div className="small text-muted">
                                Already includes <span className="text-dark fw-semibold">{bundleClasses}</span>
                              </div>
                            )}
                          </td>
                          <td>
                            {isCreate && (
                              <div className="small">
                                <span className="text-muted">Will assign: </span>
                                <span className="fw-semibold text-dark">{item.newStudentTypes || "None"}</span>
                              </div>
                            )}
                            {isUpdate && (
                              <div className="small">
                                {item.studentTypesToAdd && item.studentTypesToAdd.length > 0 ? (
                                  <>
                                    <div>
                                      <span className="text-muted">Adding: </span>
                                      <span className="badge bg-info text-white me-1">
                                        +{item.studentTypesToAdd.join(", ")}
                                      </span>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                      Current: <span className="text-dark">{item.currentStudentTypes}</span> &rarr; Result:{" "}
                                      <span className="fw-semibold text-dark">{item.newStudentTypes}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-muted">
                                    Same types (<span className="text-dark">{item.currentStudentTypes}</span>)
                                  </div>
                                )}
                              </div>
                            )}
                            {isNoChange && (
                              <div className="small text-muted">
                                Already includes <span className="text-dark fw-semibold">{targetStudentTypes}</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 bg-light d-flex justify-content-between">
            <span className="text-muted small">
              Total items: <span className="fw-bold">{summary.total || items.length}</span>
            </span>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary px-3"
                disabled={syncing}
                onClick={onClose}
              >
                {hasChanges ? "Cancel" : "Close"}
              </button>
              {hasChanges && (
                <button
                  type="button"
                  className="btn btn-primary px-4 fw-semibold d-flex align-items-center gap-2"
                  disabled={syncing}
                  onClick={onConfirm}
                >
                  {syncing ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle"></i> Confirm & Sync
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncPreviewModal;
